// Cookie-based wishlist ("Merkliste"): a deduplicated list of (type, id) entries, persisted
// client-side in a cookie (explicit product choice, not localStorage - see chat) so it works the
// same way across tabs/sessions on this browser. No server-side persistence and no dependency on
// the logged-in identity - simple by design, further Merkliste features (export, sharing, ...)
// are explicitly out of scope for now.
//
// Hybrid: an entry is either a product (id = artNr) or a document (id = documentId, falling back
// to assetId for the rare document with no resolvable Document_central - see
// MediaItem.documentId) - matches the legacy DOPI application, where individual documents can be
// starred independently of their owning product.

import { atom, getDefaultStore, useAtom } from 'jotai';

export type WishlistItemType = 'product' | 'document';

export interface WishlistKey {
    type: WishlistItemType;
    id: string;
}

const COOKIE_NAME = 'pih_wishlist';
const COOKIE_MAX_AGE_DAYS = 365;

const readCookie = (name: string): string | undefined => {
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : undefined;
};

const writeCookie = (name: string, value: string, maxAgeDays: number): void => {
    const maxAgeSeconds = maxAgeDays * 24 * 60 * 60;
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${maxAgeSeconds}; SameSite=Lax`;
};

// Conservative safety margin under the ~4093-byte ceiling most browsers enforce per cookie
// (name + value + attributes combined) - once a write would cross it, the browser silently
// drops the whole thing, which previously left the in-memory list (and the UI) showing an item
// as "saved" right up until the next reload reverted it. See `persist` below, which refuses the
// mutation instead once this limit would be exceeded.
const COOKIE_SAFE_BYTES = 3800;
const fitsInCookie = (name: string, value: string): boolean =>
    `${name}=${encodeURIComponent(value)}`.length <= COOKIE_SAFE_BYTES;

// "product:586214000" / "document:9617ef2b-54a0-4e63-a959-a937a0809ea3" - a plain prefix scheme
// keeps the cookie a flat comma-list (no JSON, no escaping concerns for the ids involved: article
// numbers and STIBO stepIds/assetIds never contain ":" or ",").
const encodeKey = (key: WishlistKey): string => `${key.type}:${key.id}`;

const decodeKey = (raw: string): WishlistKey | null => {
    const sep = raw.indexOf(':');
    if (sep < 0) {
        return null;
    }
    const type = raw.slice(0, sep);
    const id = raw.slice(sep + 1);
    if (!id || (type !== 'product' && type !== 'document')) {
        return null;
    }
    return { type, id };
};

const sameKey = (a: WishlistKey, b: WishlistKey) => a.type === b.type && a.id === b.id;
const sameList = (a: WishlistKey[], b: WishlistKey[]) => a.length === b.length && a.every((k, i) => sameKey(k, b[i]));

const parseWishlistCookie = (): WishlistKey[] => {
    const raw = readCookie(COOKIE_NAME);
    if (!raw) {
        return [];
    }
    return raw
        .split(',')
        .map((entry) => decodeKey(entry.trim()))
        .filter((key): key is WishlistKey => key !== null);
};

// Initialized once from the cookie when this module first loads - fine for a client-only SPA
// (no server-side rendering here, `document` is always available).
export const wishlistAtom = atom<WishlistKey[]>(parseWishlistCookie());

// Cross-tab resync: this atom is seeded only once, from whichever tab's OWN module load first
// read the cookie - cookies don't fire a `storage` event the way localStorage does, so a write
// made in another tab never reaches it on its own. Re-reading the cookie whenever THIS tab
// regains focus/visibility covers the common case (edit the Merkliste in tab A, switch back to
// tab B) without a full cross-tab merge protocol for a feature this simple. Registered once here
// at module scope - not inside useWishlist below, which every WishlistButton calls independently
// - so there is exactly one listener pair regardless of how many are mounted at once.
const resyncFromCookie = () => {
    const store = getDefaultStore();
    const fresh = parseWishlistCookie();
    if (!sameList(store.get(wishlistAtom), fresh)) {
        store.set(wishlistAtom, fresh);
    }
};
document.addEventListener('visibilitychange', resyncFromCookie);
window.addEventListener('focus', resyncFromCookie);

export interface UseWishlistResult {
    items: WishlistKey[];
    has: (type: WishlistItemType, id: string) => boolean;
    add: (type: WishlistItemType, id: string) => void;
    remove: (type: WishlistItemType, id: string) => void;
    toggle: (type: WishlistItemType, id: string) => void;
    clear: () => void;
}

/** Read/write access to the wishlist - every mutation writes through to the cookie immediately. */
export const useWishlist = (): UseWishlistResult => {
    const [items, setItems] = useAtom(wishlistAtom);

    const persist = (next: WishlistKey[]) => {
        const encoded = next.map(encodeKey).join(',');
        if (!fitsInCookie(COOKIE_NAME, encoded)) {
            // Refuse the whole mutation (atom included) rather than update the in-memory list
            // while the cookie write silently fails - otherwise the UI would show an item as
            // saved that a reload then reverts, with no indication anything went wrong.
            // eslint-disable-next-line no-console
            console.warn('Merkliste: Eintrag wurde nicht gespeichert (Cookie-Kapazität erreicht).');
            return;
        }
        setItems(next);
        writeCookie(COOKIE_NAME, encoded, COOKIE_MAX_AGE_DAYS);
    };

    const has = (type: WishlistItemType, id: string) => items.some((i) => i.type === type && i.id === id);
    const add = (type: WishlistItemType, id: string) => {
        if (!has(type, id)) {
            persist([...items, { type, id }]);
        }
    };
    const remove = (type: WishlistItemType, id: string) => persist(items.filter((i) => !sameKey(i, { type, id })));
    const toggle = (type: WishlistItemType, id: string) => (has(type, id) ? remove(type, id) : add(type, id));
    const clear = () => persist([]);

    return { items, has, add, remove, toggle, clear };
};
